import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Clock, 
  ChevronLeft, 
  ChevronRight, 
  Globe,
  Calendar as CalendarIcon,
  Check,
  Loader2,
  Send,
  CalendarPlus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const DURATION_OPTIONS = [
  { value: 30, label: "Quick", sublabel: "30 min" },
  { value: 60, label: "Standard", sublabel: "60 min" },
  { value: 90, label: "Deep Dive", sublabel: "90 min" },
];

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Mexico_City", label: "Mexico City" },
  { value: "Europe/London", label: "London (GMT)" },
  { value: "Europe/Paris", label: "Paris (CET)" },
  { value: "Asia/Dubai", label: "Dubai (GST)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "UTC", label: "UTC" },
];

export const AvailabilityCalendar = ({ 
  expertId, 
  expertName,
  hourlyRate,
  currency = "USD",
  onSlotSelect,
  selectedSlot = null
}) => {
  const { user } = useAuth();
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestingSlot, setRequestingSlot] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState(60);
  // Auto-detect user's timezone on initial load
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Check if user's timezone is in our list, otherwise default to closest match or UTC
    const tzInList = TIMEZONES.find(tz => tz.value === userTz);
    return tzInList ? userTz : "UTC";
  });
  const [visibleDays, setVisibleDays] = useState(0); // Start index for visible days
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    fetchAvailability();
  }, [expertId, selectedTimezone]);

  const fetchAvailability = async () => {
    setLoading(true);
    try {
      // Get user's local timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await axios.get(`${API}/experts/${expertId}/availability?days=14&user_timezone=${encodeURIComponent(userTimezone)}`);
      setAvailability(response.data);
      // Auto-select first available date
      if (response.data.availability?.length > 0) {
        setSelectedDate(response.data.availability[0].date);
      }
    } catch (error) {
      console.error("Error fetching availability:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (curr) => {
    const symbols = { USD: '$', EUR: '€', GBP: '£', AED: 'AED ', INR: '₹' };
    return symbols[curr] || '$';
  };

  const calculatePrice = () => {
    return (hourlyRate * selectedDuration / 60).toFixed(0);
  };

  const handleRequestTimeSlot = async (timeframe) => {
    if (!user) {
      toast.error("Please sign in to request a time slot");
      return;
    }
    
    setRequestingSlot(true);
    try {
      const token = localStorage.getItem("expertbase_token");
      await axios.post(`${API}/experts/${expertId}/request-slot`, {
        timeframe,
        duration_minutes: selectedDuration,
        user_timezone: selectedTimezone,
        message: `Looking for a ${selectedDuration} min session ${timeframe === '1_week' ? 'within the next week' : 'within the next two weeks'}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Time slot request sent! The expert will contact you soon.");
    } catch (error) {
      console.error("Error requesting slot:", error);
      toast.error("Failed to send request. Please try again.");
    } finally {
      setRequestingSlot(false);
    }
  };

  // Get visible days (show 5 at a time on desktop, 3 on mobile)
  const getVisibleDays = () => {
    if (!availability?.availability) return [];
    return availability.availability.slice(visibleDays, visibleDays + 5);
  };

  const canGoBack = visibleDays > 0;
  const canGoForward = availability?.availability && visibleDays + 5 < availability.availability.length;

  const getSelectedDaySlots = () => {
    if (!availability?.availability || !selectedDate) return [];
    const day = availability.availability.find(d => d.date === selectedDate);
    return day?.slots || [];
  };

  // Check if there are ANY available slots across all days
  const hasAnyAvailableSlots = () => {
    if (!availability?.availability) return false;
    return availability.availability.some(day => 
      day.slots.some(slot => slot.available)
    );
  };

  const getDayLabel = (day) => {
    if (day.is_today) return "Today";
    if (day.is_tomorrow) return "Tomorrow";
    return day.day_name.slice(0, 3);
  };

  if (loading) {
    return (
      <div className="bg-charcoal border border-slate-800 rounded-sm p-8">
        <div className="flex items-center justify-center gap-3 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading availability...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-charcoal border border-slate-800 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="bg-slate-900/50 border-b border-slate-800 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-gold-500" />
            Select a Time
          </h3>
          {availability?.calendar_connected && (
            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full flex items-center gap-1">
              <Check className="w-3 h-3" />
              Calendar synced
            </span>
          )}
        </div>

        {/* Timezone Selector */}
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-slate-500" />
          <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
            <SelectTrigger className="w-full bg-slate-900 border-slate-700 text-sm h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {TIMEZONES.map(tz => (
                <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Duration Options */}
      <div className="p-4 border-b border-slate-800">
        <p className="text-xs text-slate-500 mb-3 uppercase tracking-wide">Session Duration</p>
        <div className="grid grid-cols-3 gap-2">
          {DURATION_OPTIONS.map(option => (
            <button
              key={option.value}
              onClick={() => setSelectedDuration(option.value)}
              className={`p-3 rounded-sm border transition-all ${
                selectedDuration === option.value
                  ? "border-gold-500 bg-gold-500/10 text-gold-500"
                  : "border-slate-700 hover:border-slate-600 text-slate-300"
              }`}
            >
              <div className="font-medium text-sm">{option.label}</div>
              <div className="text-xs opacity-70">{option.sublabel}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Date Selector */}
      <div className="p-4 border-b border-slate-800">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Select Date</p>
          <div className="flex gap-1">
            <button
              onClick={() => setVisibleDays(Math.max(0, visibleDays - 5))}
              disabled={!canGoBack}
              className={`p-1 rounded ${canGoBack ? 'hover:bg-slate-700 text-slate-400' : 'text-slate-700 cursor-not-allowed'}`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setVisibleDays(visibleDays + 5)}
              disabled={!canGoForward}
              className={`p-1 rounded ${canGoForward ? 'hover:bg-slate-700 text-slate-400' : 'text-slate-700 cursor-not-allowed'}`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-2">
          {getVisibleDays().map(day => {
            const availableCount = day.slots.filter(s => s.available).length;
            const isSelected = selectedDate === day.date;
            
            return (
              <button
                key={day.date}
                onClick={() => setSelectedDate(day.date)}
                className={`p-2 rounded-sm border text-center transition-all ${
                  isSelected
                    ? "border-gold-500 bg-gold-500/10"
                    : availableCount > 0
                      ? "border-slate-700 hover:border-gold-500/50"
                      : "border-slate-700 hover:border-orange-500/50"
                }`}
              >
                <div className={`text-xs font-medium ${isSelected ? 'text-gold-500' : 'text-slate-400'}`}>
                  {getDayLabel(day)}
                </div>
                <div className={`text-lg font-display ${isSelected ? 'text-gold-500' : 'text-white'}`}>
                  {day.display_date.split(' ')[1]}
                </div>
                <div className={`text-xs ${availableCount > 0 ? 'text-green-400' : 'text-orange-400'}`}>
                  {availableCount > 0 ? `${availableCount} slots` : 'Full'}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Time Slots */}
      <div className="p-4">
        <p className="text-xs text-slate-500 mb-3 uppercase tracking-wide">Available Times</p>
        
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedDate}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto custom-scrollbar"
          >
            {getSelectedDaySlots().map((slot, idx) => {
              const isSelected = selectedSlot?.date === selectedDate && selectedSlot?.time === slot.time;
              
              return (
                <button
                  key={idx}
                  onClick={() => slot.available && onSlotSelect?.({ 
                    date: selectedDate, 
                    time: slot.time,
                    time_12h: slot.time_12h,
                    duration: selectedDuration
                  })}
                  disabled={!slot.available}
                  className={`py-2 px-3 rounded-sm text-sm font-medium transition-all ${
                    isSelected
                      ? "bg-gold-500 text-black"
                      : slot.available
                        ? "bg-slate-800 hover:bg-gold-500/20 hover:text-gold-500 text-slate-300 border border-slate-700 hover:border-gold-500/50"
                        : "bg-slate-900 text-slate-600 cursor-not-allowed line-through"
                  }`}
                >
                  {slot.time_12h}
                </button>
              );
            })}
          </motion.div>
        </AnimatePresence>

        {getSelectedDaySlots().filter(s => s.available).length === 0 && (
          <div className="text-center py-6 text-slate-500">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="mb-4">No available slots on this day</p>
            
            {/* Request Time Slot Section */}
            <div className="bg-slate-800/50 rounded-lg p-4 mt-4 border border-slate-700">
              <div className="flex items-center justify-center gap-2 mb-3">
                <CalendarPlus className="w-5 h-5 text-gold-500" />
                <span className="text-white font-medium">Request a Time Slot</span>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Can't find a suitable time? Request availability from the expert.
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-gold-500/30 text-gold-500 hover:bg-gold-500/10"
                  onClick={() => handleRequestTimeSlot('1_week')}
                  disabled={requestingSlot}
                >
                  {requestingSlot ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Within Next Week
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                  onClick={() => handleRequestTimeSlot('2_weeks')}
                  disabled={requestingSlot}
                >
                  {requestingSlot ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Within Next 2 Weeks
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer with Price */}
      <div className="bg-slate-900/50 border-t border-slate-800 p-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-slate-400 text-sm">Session Total</span>
            <div className="font-display text-2xl text-gold-500">
              {getCurrencySymbol(currency)}{calculatePrice()}
            </div>
            <span className="text-xs text-slate-500">{selectedDuration} min session</span>
          </div>
          {selectedSlot && (
            <div className="text-right">
              <div className="text-sm text-slate-400">Selected</div>
              <div className="font-medium text-white">{selectedSlot.time_12h}</div>
              <div className="text-xs text-slate-500">
                {availability?.availability?.find(d => d.date === selectedSlot.date)?.display_date}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
